import { VtmnAlert } from '@vtmn/react';
import { VtmnAlertVariant } from '@vtmn/react/dist/src/components/overlays/VtmnAlert/types';
import { createContext, ReactNode, useContext, useState } from 'react';
import { v4 } from 'uuid';

type ToastContextType = {
  addToast: (
    title: string,
    variant: VtmnAlertVariant,
    timeout: number,
    dataTestId: string,
  ) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

type ToastProviderProps = {
  children: ReactNode;
};

type Toast = {
  id: string;
  title: string;
  variant: VtmnAlertVariant;
  timeout: number;
  dataTestId: string;
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<Toast[] | []>([]);

  const addToast = (
    title: string,
    variant: VtmnAlertVariant,
    timeout: number,
    dataTestId: string,
  ) => {
    const UUID = v4();
    const toast = { id: UUID, title, variant, timeout, dataTestId };
    setToasts([toast, ...toasts]);
  };

  const removeToast = (id: string) => {
    setToasts([...toasts].filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      <div className="toasts-container">
        {toasts.map((toast) => (
          <VtmnAlert
            title={toast.title}
            variant={toast.variant}
            key={`${toast.id}`}
            ariaLabelCloseButton="Close"
            timeout={toast.timeout}
            data-testid={toast.dataTestId}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast should be used inside a ToastProvider');
  }
  return context;
};
